/**
 * Memora theme interactivity — no build step, no framework, plain DOM APIs.
 * Everything here progressively enhances markup that already works without
 * JS (native <form> posts, real hrefs) except the variant price/option
 * live-update and the shop filter, which have no non-JS equivalent.
 */
(function () {
  'use strict';

  // ---- Money formatting (mirrors Shopify's `money` filter using the
  // shop's own format string, set on <body> in layout/theme.liquid) ----
  function formatMoney(cents, format) {
    format = format || window.memShopMoneyFormat || '₱{{amount}}';
    var amount = (cents / 100).toFixed(2);
    var noDecimals = String(Math.round(cents / 100));
    var parts = amount.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amountWithComma = withCommas + '.' + parts[1];
    var noDecimalsComma = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace('{{amount}}', amountWithComma)
      .replace('{{amount_no_decimals}}', noDecimalsComma)
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimalsComma);
  }

  function priceHtml(amountCents, compareAtCents) {
    var sale = compareAtCents && compareAtCents > amountCents;
    var currency = window.memShopCurrency || 'PHP';
    var html =
      '<span class="mem-price mem-price--lg' + (sale ? ' mem-price--sale' : '') + '">' +
      '<span class="mem-price__amount">' + formatMoney(amountCents) +
      '<span class="mem-price__currency">' + currency + '</span></span>';
    if (sale) {
      html += '<span class="mem-price__compare">' + formatMoney(compareAtCents) + '</span>';
    }
    html += '</span>';
    return html;
  }

  // ---- Product variant picker ----
  function initProductForms() {
    document.querySelectorAll('[data-mem-product]').forEach(function (root) {
      var jsonEl = root.querySelector('[data-mem-product-json]');
      if (!jsonEl) return;
      var variants;
      try {
        variants = JSON.parse(jsonEl.textContent);
      } catch (e) {
        return;
      }

      var form = root.querySelector('form');
      var variantInput = root.querySelector('[data-mem-variant-id]');
      var priceEl = root.querySelector('[data-mem-price]');
      var perPieceEl = root.querySelector('[data-mem-per-piece]');
      var setHintEl = root.querySelector('[data-mem-set-hint]');
      var addBtn = form ? form.querySelector('.mem-btn[type="submit"]') : null;
      var mainImage = root.querySelector('[data-mem-main-image] img');
      var resultEl = root.querySelector('[data-mem-add-result]');

      // Current selection, seeded from whichever option buttons are marked
      // selected server-side (Shopify already resolved ?variant= there).
      var selected = {};
      root.querySelectorAll('[data-option-values]').forEach(function (group) {
        var idx = group.getAttribute('data-option-index');
        var active = group.querySelector('.mem-tag.is-selected');
        if (active) selected[idx] = active.getAttribute('data-option-value');
      });

      function findVariant() {
        var keys = Object.keys(selected);
        return variants.filter(function (v) {
          return keys.every(function (idx) {
            var optKey = 'option' + (parseInt(idx, 10) + 1);
            return v[optKey] === selected[idx];
          });
        })[0];
      }

      function render() {
        var variant = findVariant();
        if (!variant) return;

        if (variantInput) variantInput.value = variant.id;

        if (priceEl) priceEl.innerHTML = priceHtml(variant.price, variant.compare_at_price);

        // "Set of" hint + per-magnet price — the option group whose values
        // are pure numbers is the set-size axis.
        root.querySelectorAll('[data-mem-option-block]').forEach(function (block) {
          var idx = block.getAttribute('data-option-index');
          var setValue = selected[idx];
          if (block.querySelector('[data-mem-per-piece]') && setValue) {
            var per = Math.round(variant.price / 100 / parseFloat(setValue));
            var perEl = block.querySelector('[data-mem-per-piece]');
            if (perEl) perEl.textContent = '₱' + per + ' per magnet';
            var hintEl = block.querySelector('[data-mem-set-hint]');
            if (hintEl) hintEl.textContent = setValue + ' magnets · ' + setValue + ' photos';
          }
        });

        if (mainImage && variant.featured_image && variant.featured_image.src) {
          mainImage.src = variant.featured_image.src;
        }

        if (addBtn) {
          if (variant.available) {
            addBtn.removeAttribute('disabled');
            addBtn.classList.remove('is-disabled');
            addBtn.querySelector('span').textContent = 'Add to cart';
          } else {
            addBtn.setAttribute('disabled', 'disabled');
            addBtn.classList.add('is-disabled');
            addBtn.querySelector('span').textContent = 'Sold out';
          }
        }

        // Keep the URL shareable/bookmarkable without a page reload.
        if (window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      }

      root.querySelectorAll('[data-option-values] .mem-tag').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx = btn.getAttribute('data-option-index');
          var group = btn.closest('[data-option-values]');
          group.querySelectorAll('.mem-tag').forEach(function (b) {
            b.classList.remove('is-selected');
          });
          btn.classList.add('is-selected');
          selected[idx] = btn.getAttribute('data-option-value');
          render();
        });
      });

      // ---- Add to cart (AJAX; falls back to a plain POST if JS fails) ----
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var formData = new FormData(form);
          var body = {
            id: formData.get('id'),
            quantity: parseInt(formData.get('quantity'), 10) || 1
          };
          if (addBtn) addBtn.setAttribute('disabled', 'disabled');
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          })
            .then(function (res) {
              return res.json().then(function (data) {
                return { ok: res.ok, data: data };
              });
            })
            .then(function (result) {
              if (addBtn) addBtn.removeAttribute('disabled');
              if (resultEl) {
                if (result.ok) {
                  resultEl.innerHTML =
                    '<div class="mem-notice mem-notice--success">' +
                    renderIcon('circle-check') +
                    '<div><div class="mem-notice__title">Added to your cart</div>' +
                    '<div class="mem-notice__body">We\'ll message you on Messenger or email with your layout preview – usually within a few hours.</div></div></div>';
                  refreshCartCount();
                } else {
                  var message = (result.data && result.data.description) || 'Something went wrong adding that to your cart.';
                  resultEl.innerHTML =
                    '<div class="mem-notice mem-notice--error">' +
                    renderIcon('circle-alert') +
                    '<div><div class="mem-notice__title">Couldn\'t add that</div>' +
                    '<div class="mem-notice__body">' + escapeHtml(message) + '</div></div></div>';
                }
              }
            })
            .catch(function () {
              if (addBtn) addBtn.removeAttribute('disabled');
              form.submit(); // last resort: plain form POST to /cart/add
            });
        });
      }
    });
  }

  function renderIcon(name) {
    // Minimal inline copies of the two icons used in JS-generated notices —
    // keeps this file dependency-free from the Liquid-rendered snippet.
    if (name === 'circle-check') {
      return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.3 2.3 4.7-5.1"/></svg>';
    }
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8v5"/><path d="M12 16h.01"/></svg>';
  }

  function escapeHtml(s) {
    var div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  function refreshCartCount() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-mem-cart-badge]').forEach(function (badge) {
          badge.textContent = cart.item_count;
          badge.hidden = cart.item_count === 0;
        });
      })
      .catch(function () {});
  }

  // ---- Quantity stepper ----
  function initQuantitySteppers() {
    document.addEventListener('click', function (e) {
      var decBtn = e.target.closest('[data-quantity-decrease]');
      var incBtn = e.target.closest('[data-quantity-increase]');
      if (!decBtn && !incBtn) return;
      var wrapper = (decBtn || incBtn).closest('[data-quantity-wrapper]');
      var input = wrapper.querySelector('[data-quantity-input]');
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var value = parseInt(input.value, 10) || min;
      value = decBtn ? Math.max(min, value - 1) : value + 1;
      input.value = value;
    });
  }

  // ---- Photo drop (simulated — see snippets/photo-drop.liquid) ----
  function initPhotoDrop() {
    document.querySelectorAll('[data-photo-drop]').forEach(function (el) {
      var count = 0;
      el.addEventListener('click', function () {
        count += 3;
        var title = el.querySelector('[data-photo-drop-title]');
        if (title) title.textContent = count + ' photo' + (count === 1 ? '' : 's') + ' ready';
      });
    });
  }

  // ---- Product gallery thumbnails ----
  function initGallery() {
    document.querySelectorAll('[data-mem-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var root = btn.closest('[data-mem-product]');
        var main = root.querySelector('[data-mem-main-image] img');
        if (main) main.src = btn.getAttribute('data-full-src');
        root.querySelectorAll('[data-mem-thumb]').forEach(function (b) {
          b.classList.remove('is-active');
        });
        btn.classList.add('is-active');
      });
    });
  }

  // ---- Shop page filters ----
  function initFilters() {
    document.querySelectorAll('[data-mem-filterbar]').forEach(function (bar) {
      var grid = document.querySelector('[data-mem-catalogue-grid]');
      if (!grid) return;
      var countEl = bar.closest('.mem-filterbar').querySelector('[data-mem-filter-count]');
      var items = grid.querySelectorAll('[data-mem-group]');

      function apply(filter) {
        var shown = 0;
        items.forEach(function (item) {
          var match = filter === 'All' || item.getAttribute('data-mem-group') === filter;
          item.style.display = match ? '' : 'none';
          if (match) shown++;
        });
        if (countEl) countEl.textContent = shown + (shown === 1 ? ' product' : ' products');
      }

      bar.querySelectorAll('[data-mem-filter]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          bar.querySelectorAll('[data-mem-filter]').forEach(function (b) {
            b.classList.remove('is-selected');
          });
          btn.classList.add('is-selected');
          apply(btn.getAttribute('data-mem-filter'));
        });
      });

      apply('All');
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initProductForms();
    initQuantitySteppers();
    initPhotoDrop();
    initGallery();
    initFilters();
  });
})();
